import React from "react";
import { Button as RawButton } from "@/components/ui/button";
import { ChevronLeft, MapPin, Star, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import CoursePhotoGallery from "./CoursePhotoGallery";

/**
 * Read-only course profile — informational only, no date/tee-time/player
 * picker and no checkout. "Golf Game" was renamed "About Courses" and this
 * detail view no longer leads anywhere but back to the course list.
 *
 * @param {{
 *   course: {
 *     image: string;
 *     name: string;
 *     city: string;
 *     country: string;
 *     rating: number;
 *     description: string;
 *     images?: string[];
 *     difficulty?: string;
 *     fees_included?: string[];
 *     amenities?: string[];
 *   };
 *   onBack: () => void;
 * }} props
 */

export default function CourseDetail({ course, onBack }) {
  const Button = /** @type {React.ComponentType<any>} */ (RawButton);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-gray-100 sticky top-0 z-10 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={onBack}
            className="gap-2 -ml-2 text-[#0B2421] hover:bg-transparent hover:text-[#0B2421]/70 hover:underline">
            <ChevronLeft className="h-4 w-4" />
            Back to Courses
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Hero Image */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative h-[400px] rounded-2xl overflow-hidden shadow-xl">

          <img
            src={course.image}
            alt={course.name}
            className="w-full h-full object-cover" />

          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
            <h1 className="text-4xl font-bold mb-3">{course.name}</h1>
            <div className="flex items-center gap-4 text-lg">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                <span>{course.city}, {course.country}</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="h-5 w-5 fill-amber-400 text-amber-400" />
                <span className="font-semibold">{course.rating}</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Photo Gallery */}
        {course.images && course.images.length > 0 &&
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}>

            <CoursePhotoGallery course={course} />
          </motion.div>
        }

        {/* Description */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}>
          <div className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
            <h2 className="text-2xl font-bold mb-4 text-[#0B2421]">About This Course</h2>
            <p className="leading-relaxed text-gray-600 whitespace-pre-line">{course.description}</p>
          </div>
        </motion.div>

        {/* Course Friendliness */}
        {course.difficulty != null && course.difficulty !== "" &&
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.12 }}>
            <div className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
              <h2 className="text-2xl font-bold mb-4 text-[#0B2421]">Course Friendliness</h2>
              <p className="leading-relaxed text-gray-600 whitespace-pre-line">{course.difficulty}</p>
            </div>
          </motion.div>
        }

        {/* Fees Included */}
        {course.fees_included && course.fees_included.length > 0 &&
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}>
            <div className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
              <h2 className="text-2xl font-bold mb-4 text-[#0B2421]">Included in Fees</h2>
              <div className="grid grid-cols-2 gap-3">
                {course.fees_included?.map((fee, index) =>
              <div key={index} className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-emerald-600" />
                    <span className="text-gray-700">{fee}</span>
                  </div>
              )}
              </div>
            </div>
          </motion.div>
        }

        {/* Amenities */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}>
          <div className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
            <h2 className="text-2xl font-bold mb-4 text-[#0B2421]">Amenities & Features</h2>
            <div className="grid grid-cols-2 gap-3">
              {course.amenities?.map((amenity, index) =>
              <div key={index} className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-emerald-600" />
                  <span className="text-gray-700">{amenity}</span>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>);

}
